import React, { useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  Modal,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { pickImageFromLibrary, requestNotificationPermission } from '../utils/permissions';
import TonyteCropModal from '../components/TonyteCropModal';
import Icon from 'react-native-vector-icons/Feather';
import { updateProfile as updateAuthProfile } from 'firebase/auth';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import TonyteBackground from '../components/TonyteBackground';
import PickerField from '../components/PickerField';
import { Colors, Radius, Shadow, Typography } from '../theme';
import { auth, FIRESTORE_COLLECTIONS, firestore, uploadUserImage } from '../firebase';
import { BUILT_IN_INTEREST_KEYS, getInterestCategoryTranslationKey, getInterestTranslationKey, INTEREST_CATEGORIES } from '../data/interests';
import type { RootStackParamList, TonyteUser } from '../types';
import { buildCompletionPatch, computeCompletionScore } from '../utils/profileCompletion';
import { MIN_AGE, isDateOfBirthValid } from '../utils/age';
import { LANGUAGE_LABELS, SUPPORTED_LANGUAGES, SupportedLanguage, useI18n } from '../i18n';
import OnboardingThemeSelection from './OnboardingThemeSelection';
import { useTheme } from '../theme/useTheme';
import type { ThemeName } from '../theme/themes';
import LegalLink from '../components/LegalLink';

const TERMS_URL = 'https://tonyte.app/terms';
const PRIVACY_URL = 'https://tonyte.app/privacy';

type Props = NativeStackScreenProps<RootStackParamList, 'ProfileSetup'>;
type SetupStep = 1 | 2 | 3 | 4 | 5 | 6 | 7;
const LANGUAGE_CODES: Record<SupportedLanguage, string> = { en: 'EN', de: 'DE', es: 'ES', fr: 'FR', ru: 'RU' };

const TERMS_VERSION = '1.0';

const ProfileSetupScreen: React.FC<Props> = ({ route, navigation }) => {
  const { uid, email, name: initialName, photo: initialPhoto } = route.params;
  const insets = useSafeAreaInsets();
  const { t, language, setLanguage } = useI18n();
  const { setThemeName } = useTheme();
  const [step, setStep] = useState<SetupStep>(1);
  const [selectedTheme, setSelectedTheme] = useState<ThemeName | null>(null);
  const [displayName, setDisplayName] = useState(initialName?.trim() || '');
  const [photoUri, setPhotoUri] = useState('');
  const [photoSelected, setPhotoSelected] = useState(false);
  const [cropUri, setCropUri] = useState('');
  const [showCrop, setShowCrop] = useState(false);
  void initialPhoto;
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [selectedInterests, setSelectedInterests] = useState<string[]>(['Travel']);
  const [interestSearch, setInterestSearch] = useState('');
  const [skipWarningOpen, setSkipWarningOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Step 4 — student verification
  const [studentEmail, setStudentEmail] = useState('');
  // Step 5 — terms
  const [termsAgreed, setTermsAgreed] = useState(false);

  const activeIcon =
    step === 1 ? 'globe' :
    step === 2 ? 'user' :
    step === 3 ? 'camera' :
    step === 4 ? 'shield' :
    step === 5 ? 'award' :
    'check-circle';
  const filteredCategories = useMemo(() => {
    const query = interestSearch.trim().toLowerCase();
    return INTEREST_CATEGORIES
      .map(category => ({
        ...category,
        interests: category.interests.filter(interest => (
          interest.toLowerCase().includes(query)
          || t(getInterestTranslationKey(interest)).toLowerCase().includes(query)
        )),
      }))
      .filter(category => !query || category.interests.length > 0);
  }, [interestSearch, t]);
  const customInterests = selectedInterests.filter(interest => !BUILT_IN_INTEREST_KEYS.has(interest.toLowerCase()));
  const SetupScroller = Platform.OS === 'web' ? View : ScrollView;

  const patchProfile = async (patch: Record<string, unknown>) => {
    // Always write to the currently authenticated user's document, never a
    // stale uid from route params that may differ after a token refresh.
    const effectiveUid = auth.currentUser?.uid ?? uid;
    if (!effectiveUid) {
      throw new Error('No authenticated user — cannot save profile data.');
    }
    const path = `${FIRESTORE_COLLECTIONS.users}/${effectiveUid}`;
    try {
      await setDoc(doc(firestore, FIRESTORE_COLLECTIONS.users, effectiveUid), {
        id: effectiveUid,
        email: auth.currentUser?.email ?? email,
        ...patch,
        updatedAt: serverTimestamp(),
        lastActiveAt: new Date().toISOString(),
      }, { merge: true });
    } catch (err) {
      console.error('[Tonyte][Onboarding] Firestore write failed', {
        path,
        patchKeys: Object.keys(patch),
        effectiveUid,
        error: err instanceof Error ? err.message : String(err),
      });
      throw err;
    }
  };

  const selectOnboardingLanguage = (nextLanguage: SupportedLanguage) => {
    if (nextLanguage === language) return;
    setError('');
    setLanguage(nextLanguage).catch(() => setError(t('settings.languageSaveError')));
  };

  const saveDisplayName = async () => {
    const trimmedName = displayName.trim();
    if (trimmedName.length < 2) {
      setError(t('onboarding.displayNameMin'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      await patchProfile({ name: trimmedName, username: trimmedName });
      if (auth.currentUser?.uid === uid) {
        await updateAuthProfile(auth.currentUser, { displayName: trimmedName });
      }
      setStep(3);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : t('onboarding.displayNameSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const pickPhoto = async () => {
    const result = await pickImageFromLibrary({ mediaType: 'photo', quality: 1 });
    const uri = result?.assets?.[0]?.uri;
    if (!uri) return;
    console.log('[Tonyte][ImagePreview] selected image', uri);
    setCropUri(uri);
    setShowCrop(true);
    console.log('[Tonyte][ImagePreview] opening preview', {
      pendingImageType: 'profile',
      imagePreviewVisible: true,
    });
    return;
  };

  const handleCropDone = (croppedUri: string) => {
    console.log('[Tonyte][ImagePreview] modal closed by confirm', {
      pendingImageType: 'profile',
      uri: croppedUri,
    });
    setPhotoUri(croppedUri);
    setPhotoSelected(true);
    setError('');
    setShowCrop(false);
    setCropUri('');
  };

  const cancelImagePreview = () => {
    console.log('[Tonyte][ImagePreview] modal closed by cancel', {
      pendingImageType: 'profile',
      pendingImageUri: cropUri || null,
    });
    setShowCrop(false);
    setCropUri('');
  };

  const saveProfilePhoto = async () => {
    if (!photoSelected || !photoUri) {
      setError(t('onboarding.choosePhoto'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      const effectiveUid = auth.currentUser?.uid ?? uid;
      const { downloadUrl, storagePath } = await uploadUserImage(effectiveUid, photoUri, 'profile');
      await patchProfile({
        photo: downloadUrl,
        photoPath: storagePath,
        profileImage: downloadUrl,
        profileImagePath: storagePath,
        profilePhoto: downloadUrl,
        profilePhotoPath: storagePath,
        profilePhotoURL: downloadUrl,
        profileComplete: true,
        isProfileComplete: true,
      });
      if (auth.currentUser) {
        await updateAuthProfile(auth.currentUser, { photoURL: downloadUrl });
      }
      setStep(4);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : t('onboarding.photoSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const skipProfilePhoto = async () => {
    setSaving(true);
    setError('');
    try {
      await patchProfile({
        profilePhotoURL: null,
        profilePhoto: null,
        profileImage: null,
        photoURL: null,
        profileComplete: false,
        isProfileComplete: false,
      });
      setSkipWarningOpen(false);
      setStep(4);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : t('onboarding.photoSkipFailed'));
    } finally {
      setSaving(false);
    }
  };

  const saveFinalStep = async () => {
    if (!dateOfBirth) {
      setError(t('onboarding.dobRequired'));
      return;
    }
    if (!isDateOfBirthValid(dateOfBirth)) {
      setError(t('onboarding.ageRequirement').replace('{age}', String(MIN_AGE)));
      return;
    }
    setSaving(true);
    setError('');
    try {
      await patchProfile({
        dateOfBirth,
        interests: selectedInterests.length ? selectedInterests : ['Travel'],
        customInterests,
      });
      setStep(5);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : t('onboarding.profileSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const saveStudentVerification = async () => {
    const trimmedEmail = studentEmail.trim().toLowerCase();
    const hasEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
    setSaving(true);
    setError('');
    try {
      if (hasEmail) {
        // Call the Cloud Function — it sends a verification email and updates the user doc.
        const { callSendVerificationEmail } = await import('../utils/studentVerification');
        await callSendVerificationEmail(trimmedEmail);
      } else {
        await patchProfile({ studentVerificationSkipped: true });
      }
      setStep(6);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : t('onboarding.verificationSendFailed'));
    } finally {
      setSaving(false);
    }
  };

  const skipStudentVerification = async () => {
    setSaving(true);
    setError('');
    try {
      await patchProfile({ studentVerificationSkipped: true });
      setStep(6);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : t('onboarding.skipFailed'));
    } finally {
      setSaving(false);
    }
  };

  const acceptTermsAndEnter = async () => {
    if (!termsAgreed) {
      setError(t('onboarding.acceptTerms'));
      return;
    }
    if (!selectedTheme) {
      setError(t('onboarding.selectThemeRequired'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      // Re-save before completing onboarding so persistence is guaranteed to
      // finish before the Main navigator is allowed to render.
      await setThemeName(selectedTheme);
      await patchProfile({
        termsAccepted: true,
        termsAcceptedAt: new Date().toISOString(),
        termsVersion: TERMS_VERSION,
        onboardingComplete: true,
        profileSetupCompleted: true,
        requiresProfileSetup: false,
      });
      // Compute a partial score from the fields we know were saved during onboarding.
      // photo is represented by a non-randomuser placeholder when photoSelected is true.
      const partialProfile: Partial<TonyteUser> = {
        name: displayName.trim(),
        photo: photoSelected ? 'https://storage.googleapis.com/placeholder' : '',
        dateOfBirth,
        interests: selectedInterests,
        termsAccepted: true,
      };
      const { score, missing } = computeCompletionScore(partialProfile as TonyteUser);
      await patchProfile(buildCompletionPatch(score, missing));
      requestNotificationPermission().catch(error => {
        console.warn('[Tonyte][RN][Permissions] notification request failed', error);
      });
      navigation.replace('Main');
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : t('onboarding.setupFailed'));
    } finally {
      setSaving(false);
    }
  };

  const toggleInterest = (interest: string) => {
    setSelectedInterests(current => {
      if (current.includes(interest)) return current.filter(item => item !== interest);
      if (current.length >= 20) return current;
      return [...current, interest];
    });
  };

  const goBack = () => {
    setError('');
    setStep(current =>
      current === 7 ? 6 :
      current === 6 ? 5 :
      current === 5 ? 4 :
      current === 4 ? 3 :
      current === 3 ? 2 : 1,
    );
  };

  const selectTheme = (nextTheme: ThemeName) => {
    setSelectedTheme(nextTheme);
    setError('');
    // Update the central provider immediately so this final onboarding screen
    // is the real theme preview rather than a separate local approximation.
    void setThemeName(nextTheme);
  };

  if (step === 7) {
    return (
      <OnboardingThemeSelection
        selectedTheme={selectedTheme}
        saving={saving}
        error={error}
        onSelect={selectTheme}
        onEnter={acceptTermsAndEnter}
        onBack={goBack}
      />
    );
  }

  return (
    <TonyteBackground style={styles.container}>
      <SetupScroller
        style={Platform.OS === 'web' ? styles.webScroller : styles.scroll}
        {...(Platform.OS === 'web' ? {} : {
          contentContainerStyle: [styles.content, { paddingTop: insets.top + 24, paddingBottom: insets.bottom + 26 }],
          keyboardShouldPersistTaps: 'handled' as const,
          keyboardDismissMode: 'on-drag' as const,
          nestedScrollEnabled: true,
          showsVerticalScrollIndicator: false,
        })}
      >
        <View style={Platform.OS === 'web'
          ? [styles.content, styles.webContent, { paddingTop: insets.top + 24, paddingBottom: insets.bottom + 26 }]
          : undefined}
        >
        <View style={styles.progressRow}>
          {[1, 2, 3, 4, 5, 6, 7].map(dot => (
            <View key={dot} style={[styles.progressDot, step === dot && styles.progressDotActive, step > dot && styles.progressDotDone]} />
          ))}
        </View>

        <View>
          <View style={styles.header}>
            <View style={styles.headerLeft}>
              {step === 1 ? null : (
                <View style={styles.stepIcon}>
                  <Icon name={activeIcon} size={23} color={Colors.turquoise} />
                </View>
              )}
              <View>
                <Text style={styles.kicker}>TONYTE</Text>
                <Text style={styles.headerSub}>{t('onboarding.travelerProfile')}</Text>
              </View>
            </View>
            <View style={styles.stepBadge}>
              <Text style={styles.stepBadgeText}>{step}/7</Text>
            </View>
          </View>

          {step === 1 ? (
            <View style={styles.stepBody}>
              <View style={styles.titleBlock}>
                <Text style={styles.title}>{t('onboarding.languageTitle')}</Text>
                <Text style={styles.subtitle}>{t('onboarding.languageSub')}</Text>
              </View>
              <View style={styles.languageList}>
                {SUPPORTED_LANGUAGES.map(option => {
                  const selected = language === option;
                  return (
                    <TouchableOpacity
                      key={option}
                      style={[styles.languageCard, selected && styles.languageCardActive]}
                      onPress={() => selectOnboardingLanguage(option)}
                      activeOpacity={0.82}
                    >
                      <View style={[styles.languageCodeBadge, selected && styles.languageCodeBadgeActive]}>
                        <Text style={[styles.languageCodeText, selected && styles.languageCodeTextActive]}>
                          {LANGUAGE_CODES[option]}
                        </Text>
                      </View>
                      <Text style={[styles.languageLabel, selected && styles.languageLabelActive]}>
                        {LANGUAGE_LABELS[option]}
                      </Text>
                      <View style={[styles.languageCheck, selected && styles.languageCheckActive]}>
                        {selected ? <Icon name="check" size={13} color="#fff" /> : null}
                      </View>
                    </TouchableOpacity>
                  );
                })}
              </View>
              {error ? <Text style={styles.error}>{error}</Text> : null}
              <TouchableOpacity style={styles.orangeButton} onPress={() => setStep(2)}>
                <Text style={styles.orangeButtonText}>{t('onboarding.continue')}</Text>
              </TouchableOpacity>
            </View>
          ) : null}

          {step === 2 ? (
            <View style={styles.stepBody}>
              <View style={styles.titleBlock}>
                <Text style={styles.title}>{t('onboarding.displayNameTitle')}</Text>
                <Text style={styles.subtitle}>{t('onboarding.displayNameSub')}</Text>
              </View>
              <View>
                <Text style={styles.inputLabel}>{t('onboarding.displayNameLabel')}</Text>
                <View style={styles.inputWrap}>
                  <Icon name="user" size={18} color={Colors.turquoise} />
                  <TextInput
                    value={displayName}
                    onChangeText={text => { setDisplayName(text); setError(''); }}
                    placeholder={t('onboarding.displayNamePlaceholder')}
                    placeholderTextColor="rgba(255,255,255,0.28)"
                    style={styles.textInput}
                  />
                </View>
                <Text style={styles.helper}>{t('onboarding.changeLater')}</Text>
              </View>
              {error ? <Text style={styles.error}>{error}</Text> : null}
              <TouchableOpacity style={[styles.orangeButton, displayName.trim().length < 2 && styles.buttonDisabled]} disabled={saving || displayName.trim().length < 2} onPress={saveDisplayName}>
                {saving ? <ActivityIndicator color="#fff" /> : <Text style={styles.orangeButtonText}>{t('onboarding.continue')}</Text>}
              </TouchableOpacity>
            </View>
          ) : null}

          {step === 3 ? (
            <View style={styles.stepBody}>
              <View style={styles.titleBlock}>
                <Text style={styles.title}>{t('onboarding.photoTitle')}</Text>
                <Text style={styles.subtitle}>{t('onboarding.photoSub')}</Text>
              </View>
              <TouchableOpacity style={styles.uploadCircle} activeOpacity={0.86} onPress={pickPhoto}>
                {photoUri ? (
                  <Image source={{ uri: photoUri }} style={styles.uploadImage} />
                ) : (
                  <View style={styles.uploadPlaceholder}>
                    <View style={styles.uploadIcon}>
                      <Icon name="upload" size={24} color={Colors.turquoise} />
                    </View>
                    <Text style={styles.uploadText}>{t('onboarding.tapUpload')}</Text>
                  </View>
                )}
              </TouchableOpacity>
              {error ? <Text style={styles.error}>{error}</Text> : null}
              <TouchableOpacity style={[styles.orangeButton, !photoSelected && styles.buttonDisabled]} disabled={saving || !photoSelected} onPress={saveProfilePhoto}>
                {saving ? <ActivityIndicator color="#fff" /> : <Text style={styles.orangeButtonText}>{t('onboarding.addPhoto')}</Text>}
              </TouchableOpacity>
              <TouchableOpacity style={styles.grayButton} disabled={saving} onPress={() => setSkipWarningOpen(true)}>
                <Text style={styles.grayButtonText}>{t('onboarding.skipNow')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.backTextButton} disabled={saving} onPress={goBack}>
                <Text style={styles.backText}>{t('common.back')}</Text>
              </TouchableOpacity>
            </View>
          ) : null}

          {step === 4 ? (
            <View style={styles.stepBody}>
              <View style={styles.titleBlock}>
                <Text style={styles.title}>{t('onboarding.dobTitle')}</Text>
                <Text style={styles.subtitle}>{t('onboarding.dobSub')}</Text>
              </View>
              <View>
                <Text style={styles.inputLabel}>{t('onboarding.dobLabel')}</Text>
                <PickerField
                  value={dateOfBirth}
                  onChange={value => { setDateOfBirth(value); setError(''); }}
                  onReject={() => setError(t('onboarding.invalidDob'))}
                  placeholder={t('completeProfile.dobPlaceholder')}
                  mode="date"
                  maximumDate={new Date()}
                  noMargin
                />
                {!dateOfBirth ? (
                  <View style={styles.requiredHint} accessibilityLiveRegion="polite">
                    <Icon name="alert-circle" size={15} color="#F5A76E" />
                    <Text style={styles.requiredHintText}>{t('onboarding.dobRequired')}</Text>
                  </View>
                ) : (
                  <View style={[styles.requiredHint, styles.requiredHintComplete]} accessibilityLiveRegion="polite">
                    <Icon name="check-circle" size={15} color={Colors.turquoise} />
                    <Text style={[styles.requiredHintText, styles.requiredHintTextComplete]}>{t('onboarding.dobReady')}</Text>
                  </View>
                )}
              </View>

              <View style={styles.interestsPanel}>
                <View style={styles.interestHeader}>
                  <View>
                    <Text style={styles.interestTitle}>{t('onboarding.interests')}</Text>
                    <Text style={styles.interestCount}>{t('onboarding.selected20').replace('{count}', String(selectedInterests.length))}</Text>
                  </View>
                  <TouchableOpacity style={styles.clearButton} onPress={() => setSelectedInterests([])}>
                    <Text style={styles.clearText}>{t('common.clear')}</Text>
                  </TouchableOpacity>
                </View>
                {selectedInterests.length ? (
                  <View style={styles.selectedChips}>
                    {selectedInterests.map(interest => (
                      <TouchableOpacity key={interest} style={styles.selectedChip} onPress={() => toggleInterest(interest)}>
                        <Text style={styles.selectedChipText}>{BUILT_IN_INTEREST_KEYS.has(interest.toLowerCase()) ? t(getInterestTranslationKey(interest)) : interest}</Text>
                        <Icon name="x" size={12} color={Colors.turquoise} />
                      </TouchableOpacity>
                    ))}
                  </View>
                ) : null}
                <View style={styles.searchWrap}>
                  <Icon name="search" size={16} color="rgba(255,255,255,0.35)" />
                  <TextInput
                    value={interestSearch}
                    onChangeText={setInterestSearch}
                    placeholder={t('interests.searchInterests')}
                    placeholderTextColor="rgba(255,255,255,0.35)"
                    style={styles.searchInput}
                  />
                </View>
                {filteredCategories.map(category => (
                  <View key={category.name} style={styles.categoryBlock}>
                    <Text style={styles.categoryTitle}>{t(getInterestCategoryTranslationKey(category.name))}</Text>
                    <View style={styles.interestGrid}>
                      {category.interests.map(interest => {
                        const selected = selectedInterests.includes(interest);
                        return (
                          <TouchableOpacity key={interest} style={[styles.interestChip, selected && styles.interestChipActive]} onPress={() => toggleInterest(interest)}>
                            <Text style={[styles.interestChipText, selected && styles.interestChipTextActive]}>{t(getInterestTranslationKey(interest))}</Text>
                          </TouchableOpacity>
                        );
                      })}
                    </View>
                  </View>
                ))}
              </View>
              {error ? <Text style={styles.error}>{error}</Text> : null}
              <View style={styles.bottomRow}>
                <TouchableOpacity style={styles.bottomBackButton} disabled={saving} onPress={goBack}>
                  <Text style={styles.grayButtonText}>{t('common.back')}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.bottomOrangeButton, !dateOfBirth && styles.buttonDisabled]}
                  disabled={saving || !dateOfBirth}
                  onPress={saveFinalStep}
                  accessibilityState={{ disabled: saving || !dateOfBirth }}
                  accessibilityHint={!dateOfBirth ? t('onboarding.dobRequired') : undefined}
                >
                  {saving ? <ActivityIndicator color="#fff" /> : (
                    <>
                      <Icon name={dateOfBirth ? 'arrow-right' : 'lock'} size={15} color="#fff" />
                      <Text
                        style={[styles.orangeButtonText, styles.bottomOrangeButtonText]}
                        numberOfLines={1}
                        adjustsFontSizeToFit
                        minimumFontScale={0.72}
                      >
                        {dateOfBirth ? t('onboarding.continue') : t('onboarding.continueLocked')}
                      </Text>
                    </>
                  )}
                </TouchableOpacity>
              </View>
            </View>
          ) : null}

          {step === 5 ? (
            <View style={styles.stepBody}>
              <View style={styles.studentHeroWrap}>
                <View style={styles.studentHeroIcon}>
                  <Icon name="award" size={36} color="#ddd6fe" />
                </View>
                <View style={styles.studentGlowRing} />
              </View>
              <View style={styles.titleBlock}>
                <Text style={styles.title}>{t('onboarding.unlockStudiZone')}</Text>
                <Text style={styles.subtitle}>
                  {t('onboarding.unlockStudiZoneSub')}
                </Text>
              </View>
              <View style={styles.studentBenefitRow}>
                {[t('onboarding.campusEvents'), t('onboarding.erasmusPlans'), t('onboarding.studentChats'), t('onboarding.verifiedGuestlist')].map(benefit => (
                  <View key={benefit} style={styles.studentBenefitChip}>
                    <Icon name="check" size={11} color="#ddd6fe" />
                    <Text style={styles.studentBenefitText}>{benefit}</Text>
                  </View>
                ))}
              </View>
              <View>
                <Text style={styles.inputLabel}>{t('onboarding.universityEmail')}</Text>
                <View style={styles.inputWrap}>
                  <Icon name="mail" size={18} color="#ddd6fe" />
                  <TextInput
                    value={studentEmail}
                    onChangeText={text => { setStudentEmail(text); setError(''); }}
                    placeholder={t('onboarding.universityEmailPlaceholder')}
                    placeholderTextColor="rgba(255,255,255,0.28)"
                    style={styles.textInput}
                    keyboardType="email-address"
                    autoCapitalize="none"
                  />
                </View>
                <Text style={styles.helper}>{t('onboarding.universityEmailHelp')}</Text>
              </View>
              {error ? <Text style={styles.error}>{error}</Text> : null}
              <TouchableOpacity
                style={[styles.orangeButton, styles.purpleButton]}
                disabled={saving}
                onPress={saveStudentVerification}
              >
                {saving ? <ActivityIndicator color="#fff" /> : <Text style={styles.orangeButtonText}>{t('onboarding.submitVerification')}</Text>}
              </TouchableOpacity>
              <TouchableOpacity style={styles.grayButton} disabled={saving} onPress={skipStudentVerification}>
                <Text style={styles.grayButtonText}>{t('onboarding.skipVerification')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.backTextButton} disabled={saving} onPress={goBack}>
                <Text style={styles.backText}>{t('common.back')}</Text>
              </TouchableOpacity>
            </View>
          ) : null}

          {step === 6 ? (
            <View style={styles.stepBody}>
              <View style={styles.titleBlock}>
                <Text style={styles.title}>{t('onboarding.guidelinesTitle')}</Text>
                <Text style={styles.subtitle}>
                  {t('onboarding.guidelinesSub')}
                </Text>
              </View>
              <View style={styles.guidelinesCard}>
                {[
                  { icon: 'heart', text: t('onboarding.guidelineRespect') },
                  { icon: 'shield', text: t('onboarding.guidelineHarassment') },
                  { icon: 'check-square', text: t('onboarding.guidelineRealEvents') },
                  { icon: 'users', text: t('onboarding.guidelineSafety') },
                  { icon: 'lock', text: t('onboarding.guidelineData') },
                  { icon: 'flag', text: t('onboarding.guidelineReport') },
                ].map(item => (
                  <View key={item.text} style={styles.guidelineRow}>
                    <View style={styles.guidelineIconWrap}>
                      <Icon name={item.icon} size={15} color={Colors.turquoise} />
                    </View>
                    <Text style={styles.guidelineText}>{item.text}</Text>
                  </View>
                ))}
              </View>
              <View style={styles.termsLinks}>
                <LegalLink
                  href={TERMS_URL}
                  style={styles.termsLink}
                  onPress={() => navigation.navigate('TermsOfService')}
                >
                  <Icon name="file-text" size={13} color="#93c5fd" />
                  <Text style={styles.termsLinkText}>{t('onboarding.viewTerms')}</Text>
                </LegalLink>
                <LegalLink
                  href={PRIVACY_URL}
                  style={styles.termsLink}
                  onPress={() => navigation.navigate('PrivacyPolicy')}
                >
                  <Icon name="shield" size={13} color="#93c5fd" />
                  <Text style={styles.termsLinkText}>{t('onboarding.viewPrivacy')}</Text>
                </LegalLink>
              </View>
              <TouchableOpacity
                style={styles.checkboxRow}
                onPress={() => { setTermsAgreed(agreed => !agreed); setError(''); }}
                activeOpacity={0.82}
              >
                <View style={[styles.checkbox, termsAgreed && styles.checkboxChecked]}>
                  {termsAgreed ? <Icon name="check" size={13} color="#fff" /> : null}
                </View>
                <Text style={styles.checkboxLabel}>
                  {t('onboarding.agreeTerms')}
                </Text>
              </TouchableOpacity>
              {error ? <Text style={styles.error}>{error}</Text> : null}
              <TouchableOpacity
                style={[styles.orangeButton, !termsAgreed && styles.buttonDisabled]}
                disabled={saving || !termsAgreed}
                onPress={() => { setError(''); setStep(7); }}
              >
                {saving ? <ActivityIndicator color="#fff" /> : <Text style={styles.orangeButtonText}>{t('onboarding.continue')}</Text>}
              </TouchableOpacity>
              <TouchableOpacity style={styles.backTextButton} disabled={saving} onPress={goBack}>
                <Text style={styles.backText}>{t('common.back')}</Text>
              </TouchableOpacity>
            </View>
          ) : null}
        </View>
        </View>
      </SetupScroller>

      <Modal visible={skipWarningOpen} transparent animationType="fade" onRequestClose={() => !saving && setSkipWarningOpen(false)}>
        <View style={styles.modalOverlay}>
          <View style={styles.skipModal}>
            <Text style={styles.skipTitle}>{t('onboarding.skipPhotoTitle')}</Text>
            <Text style={styles.skipBody}>{t('onboarding.skipPhotoBody')}</Text>
            <TouchableOpacity style={styles.orangeButton} disabled={saving} onPress={() => setSkipWarningOpen(false)}>
              <Text style={styles.orangeButtonText}>{t('onboarding.uploadPicture')}</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.grayButton} disabled={saving} onPress={skipProfilePhoto}>
              {saving ? <ActivityIndicator color="#fff" /> : <Text style={styles.grayButtonText}>{t('onboarding.skipAnyway')}</Text>}
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
      {showCrop && cropUri ? (
        <TonyteCropModal
          session={{ kind: 'profile', uri: cropUri, aspectRatio: '1:1' }}
          onCrop={handleCropDone}
          onCancel={cancelImagePreview}
        />
      ) : null}
    </TonyteBackground>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Colors.bg },
  scroll: { flex: 1, minHeight: 0 },
  webScroller: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    width: '100%',
    height: '100%',
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    WebkitOverflowScrolling: 'touch',
    touchAction: 'pan-y',
    overscrollBehaviorY: 'contain',
  } as any,
  content: {
    flexGrow: 1,
    minHeight: '100%',
    justifyContent: 'flex-start',
    paddingHorizontal: 20,
  },
  webContent: { minHeight: '100%' },
  progressRow: {
    flexDirection: 'row',
    alignSelf: 'center',
    gap: 8,
    marginBottom: 20,
  },
  progressDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: 'rgba(255,255,255,0.18)',
  },
  progressDotActive: {
    width: 36,
    backgroundColor: Colors.sunset,
    shadowColor: Colors.sunset,
    shadowOpacity: 0.38,
    shadowRadius: 18,
  },
  progressDotDone: {
    backgroundColor: 'rgba(38,208,206,0.7)',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    marginBottom: 20,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  stepIcon: {
    width: 48,
    height: 48,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.08)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  kicker: {
    color: 'rgba(255,255,255,0.35)',
    fontSize: 10,
    fontWeight: Typography.extrabold,
    letterSpacing: 2.2,
  },
  headerSub: {
    marginTop: 2,
    color: 'rgba(255,255,255,0.7)',
    fontSize: Typography.sm,
    fontWeight: Typography.semibold,
  },
  stepBadge: {
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.08)',
    paddingHorizontal: 12,
    paddingVertical: 5,
  },
  stepBadgeText: {
    color: 'rgba(255,255,255,0.5)',
    fontSize: Typography.xs,
    fontWeight: Typography.bold,
  },
  stepBody: { gap: 18 },
  // Step 1 — language selection
  languageList: { gap: 10 },
  languageCard: {
    minHeight: 64,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.055)',
    paddingHorizontal: 14,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
  },
  languageCardActive: {
    borderColor: 'rgba(38,208,206,0.42)',
    backgroundColor: 'rgba(38,208,206,0.14)',
    shadowColor: Colors.turquoise,
    shadowOpacity: 0.22,
    shadowRadius: 14,
  },
  languageCodeBadge: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.08)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  languageCodeBadgeActive: {
    backgroundColor: 'rgba(38,208,206,0.2)',
  },
  languageCodeText: {
    color: 'rgba(255,255,255,0.55)',
    fontSize: Typography.xs,
    fontWeight: Typography.extrabold,
    letterSpacing: 1,
  },
  languageCodeTextActive: {
    color: Colors.turquoise,
  },
  languageLabel: {
    flex: 1,
    color: Colors.textPrimary,
    fontSize: Typography.base,
    fontWeight: Typography.bold,
  },
  languageLabelActive: {
    color: Colors.textPrimary,
  },
  languageCheck: {
    width: 26,
    height: 26,
    borderRadius: 13,
    borderWidth: 2,
    borderColor: 'rgba(255,255,255,0.2)',
    backgroundColor: 'rgba(255,255,255,0.06)',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  languageCheckActive: {
    backgroundColor: Colors.turquoise,
    borderColor: Colors.turquoise,
  },
  titleBlock: { alignItems: 'center', gap: 8 },
  title: {
    color: Colors.textPrimary,
    fontSize: 27,
    lineHeight: 32,
    fontWeight: Typography.extrabold,
    textAlign: 'center',
  },
  subtitle: {
    color: 'rgba(255,255,255,0.55)',
    fontSize: Typography.sm,
    lineHeight: 20,
    textAlign: 'center',
  },
  inputLabel: {
    color: 'rgba(255,255,255,0.35)',
    fontSize: Typography.xs,
    fontWeight: Typography.extrabold,
    letterSpacing: 1.9,
    marginBottom: 8,
  },
  inputWrap: {
    minHeight: 54,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(0,0,0,0.2)',
    paddingHorizontal: 14,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  textInput: {
    flex: 1,
    color: Colors.textPrimary,
    fontSize: Typography.base,
    paddingVertical: 0,
  },
  helper: {
    marginTop: 8,
    color: 'rgba(255,255,255,0.38)',
    fontSize: Typography.xs,
  },
  requiredHint: {
    marginTop: 9,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    alignSelf: 'flex-start',
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: 'rgba(245,167,110,0.28)',
    backgroundColor: 'rgba(242,125,38,0.10)',
    paddingHorizontal: 10,
    paddingVertical: 7,
  },
  requiredHintComplete: {
    borderColor: 'rgba(38,208,206,0.28)',
    backgroundColor: 'rgba(38,208,206,0.10)',
  },
  requiredHintText: { color: '#F5A76E', fontSize: Typography.xs, fontWeight: Typography.bold },
  requiredHintTextComplete: { color: Colors.turquoise },
  error: {
    borderRadius: 16,
    backgroundColor: 'rgba(242,125,38,0.12)',
    color: Colors.sunset,
    paddingHorizontal: 13,
    paddingVertical: 10,
    fontSize: Typography.sm,
    lineHeight: 19,
  },
  orangeButton: {
    minHeight: 54,
    borderRadius: 18,
    backgroundColor: Colors.sunset,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: Colors.sunset,
    shadowOpacity: 0.24,
    shadowRadius: 18,
  },
  orangeButtonText: {
    color: '#fff',
    fontSize: Typography.sm,
    fontWeight: Typography.extrabold,
  },
  buttonDisabled: { opacity: 0.45 },
  uploadCircle: {
    alignSelf: 'center',
    width: 160,
    height: 160,
    borderRadius: 80,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.16)',
    backgroundColor: 'rgba(255,255,255,0.08)',
    overflow: 'hidden',
    ...Shadow.card,
  },
  uploadImage: {
    width: '100%',
    height: '100%',
  },
  uploadPlaceholder: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
  },
  uploadIcon: {
    width: 56,
    height: 56,
    borderRadius: 20,
    backgroundColor: 'rgba(38,208,206,0.14)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  uploadText: {
    color: 'rgba(255,255,255,0.58)',
    fontSize: Typography.xs,
    fontWeight: Typography.bold,
  },
  grayButton: {
    minHeight: 54,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.06)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  grayButtonText: {
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    fontWeight: Typography.bold,
  },
  backTextButton: {
    alignItems: 'center',
    paddingVertical: 8,
  },
  backText: {
    color: 'rgba(255,255,255,0.42)',
    fontSize: Typography.sm,
    fontWeight: Typography.bold,
  },
  interestsPanel: {
    borderRadius: 22,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.05)',
    padding: 13,
    gap: 12,
  },
  interestHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  interestTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.md,
    fontWeight: Typography.extrabold,
  },
  interestCount: {
    marginTop: 3,
    color: 'rgba(255,255,255,0.45)',
    fontSize: Typography.xs,
  },
  clearButton: {
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    paddingHorizontal: 13,
    paddingVertical: 8,
  },
  clearText: {
    color: '#93c5fd',
    fontSize: Typography.xs,
    fontWeight: Typography.bold,
  },
  selectedChips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  selectedChip: {
    minHeight: 34,
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.24)',
    backgroundColor: 'rgba(38,208,206,0.1)',
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
  },
  selectedChipText: {
    color: Colors.textPrimary,
    fontSize: Typography.xs,
    fontWeight: Typography.bold,
  },
  searchWrap: {
    minHeight: 46,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    backgroundColor: 'rgba(255,255,255,0.06)',
    paddingHorizontal: 13,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
  },
  searchInput: {
    flex: 1,
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    paddingVertical: 0,
  },
  categoryBlock: { gap: 8 },
  categoryTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    fontWeight: Typography.extrabold,
  },
  interestGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  interestChip: {
    minHeight: 36,
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.055)',
    paddingHorizontal: 13,
    justifyContent: 'center',
  },
  interestChipActive: {
    borderColor: 'rgba(38,208,206,0.36)',
    backgroundColor: 'rgba(38,208,206,0.16)',
  },
  interestChipText: {
    color: 'rgba(255,255,255,0.72)',
    fontSize: Typography.xs,
    fontWeight: Typography.semibold,
  },
  interestChipTextActive: {
    color: Colors.turquoise,
  },
  bottomRow: {
    flexDirection: 'row',
    gap: 10,
  },
  bottomBackButton: {
    flex: 1,
    minHeight: 54,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.06)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  bottomOrangeButton: {
    flex: 1,
    minHeight: 54,
    borderRadius: 18,
    backgroundColor: Colors.sunset,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
    paddingHorizontal: 12,
  },
  bottomOrangeButtonText: {
    flexShrink: 1,
    minWidth: 0,
    textAlign: 'center',
  },
  // Step 4 — student verification
  studentHeroWrap: {
    alignSelf: 'center',
    width: 100,
    height: 100,
    alignItems: 'center',
    justifyContent: 'center',
  },
  studentHeroIcon: {
    width: 80,
    height: 80,
    borderRadius: 28,
    borderWidth: 1,
    borderColor: 'rgba(221,214,254,0.2)',
    backgroundColor: 'rgba(139,92,246,0.18)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  studentGlowRing: {
    position: 'absolute',
    width: 100,
    height: 100,
    borderRadius: 50,
    borderWidth: 1,
    borderColor: 'rgba(139,92,246,0.28)',
  },
  studentBenefitRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    justifyContent: 'center',
  },
  studentBenefitChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: 'rgba(221,214,254,0.18)',
    backgroundColor: 'rgba(139,92,246,0.1)',
    paddingHorizontal: 11,
    paddingVertical: 6,
  },
  studentBenefitText: {
    color: '#ddd6fe',
    fontSize: Typography.xs,
    fontWeight: Typography.semibold,
  },
  purpleButton: {
    backgroundColor: '#7c3aed',
    shadowColor: '#7c3aed',
  },
  // Step 5 — terms
  guidelinesCard: {
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.09)',
    backgroundColor: 'rgba(255,255,255,0.05)',
    padding: 14,
    gap: 12,
  },
  guidelineRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
  },
  guidelineIconWrap: {
    width: 28,
    height: 28,
    borderRadius: 9,
    backgroundColor: 'rgba(38,208,206,0.1)',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: 1,
  },
  guidelineText: {
    flex: 1,
    color: 'rgba(255,255,255,0.72)',
    fontSize: Typography.sm,
    lineHeight: 20,
  },
  termsLinks: {
    gap: 8,
  },
  termsLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 4,
  },
  termsLinkText: {
    color: '#93c5fd',
    fontSize: Typography.sm,
    fontWeight: Typography.medium,
    textDecorationLine: 'underline',
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    paddingVertical: 4,
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: 'rgba(255,255,255,0.25)',
    backgroundColor: 'rgba(255,255,255,0.06)',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: 1,
  },
  checkboxChecked: {
    backgroundColor: Colors.turquoise,
    borderColor: Colors.turquoise,
  },
  checkboxLabel: {
    flex: 1,
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    lineHeight: 21,
    fontWeight: Typography.medium,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.62)',
    justifyContent: 'flex-end',
    padding: 20,
  },
  skipModal: {
    borderRadius: 28,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    backgroundColor: '#111827',
    padding: 20,
    gap: 14,
  },
  skipTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.xl,
    fontWeight: Typography.extrabold,
  },
  skipBody: {
    color: 'rgba(255,255,255,0.62)',
    fontSize: Typography.sm,
    lineHeight: 21,
  },
});

export default ProfileSetupScreen;
